/**
 * Platform detection utilities.
 *
 * Wraps @capacitor/core Capacitor.* functions and exposes a small,
 * type-safe surface used across the app to fork behavior cleanly between
 * native (iOS/Android) and web targets.
 *
 * Use IS_MOBILE for cheap, top-level branches (e.g. choosing API base URL).
 * Use the helpers for runtime checks where iOS vs Android behavior differs
 * (status bar style, back button, deep links).
 *
 * NOTE: src/utils/platform.ts already provides a similar `platform` object
 * (kept for backward-compat). New code should import from here.
 */
import { Capacitor } from '@capacitor/core';

export type PlatformName = 'ios' | 'android' | 'web';

export const isNativePlatform = (): boolean => Capacitor.isNativePlatform();
export const getPlatform = (): PlatformName =>
  Capacitor.getPlatform() as PlatformName;
export const isIOS = (): boolean => Capacitor.getPlatform() === 'ios';
export const isAndroid = (): boolean => Capacitor.getPlatform() === 'android';
export const isWeb = (): boolean => Capacitor.getPlatform() === 'web';

/**
 * IS_MOBILE — true when running inside a Capacitor native shell (iOS or Android).
 *
 * Evaluated once at module init so it can be used in plain expressions
 * (e.g. config defaults). For dynamic checks inside React effects, prefer
 * isNativePlatform() to make intent obvious.
 */
export const IS_MOBILE: boolean = Capacitor.isNativePlatform();
